import Head from 'next/head';
import {
  ActivityCode,
  Content,
  ContentTypesCount,
  InlineResponse2001,
  InterestCode,
  SearchType,
  ThematicCode,
} from 'pleinchamp-api-client';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchFetching, useSearchRouteQuery } from '@search/business/search.hooks';
import {
  activityInUrl,
  contentTypeInUrl,
  interestInUrl,
  SearchContentType,
  SearchQueryParams,
  thematicInUrl,
} from '@search/business/search.types';
import { getSearchContentTypes, searchContent, SearchParams } from '@search/business/search.utils';
import { SearchLayout } from '@search/components/SearchLayout/SearchLayout.component';
import { Constants } from '@utils/Constants';
import { ExtendedPagination } from '@utils/pagination';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString, transformUrlParamToEnumArray } from '@utils/url';
import { GeneralContext } from 'features/business/app.contexts';
import { PlcNextPage, PlcNextPageContext, PlcPageContextProps } from 'features/business/app.types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

export type SearchPageProps = SearchQueryParams & { searchResults: InlineResponse2001 };
interface ContentProps {
  results: Content[];
  pagination: ExtendedPagination;
  counts: ContentTypesCount;
}

const NAMESPACES = [...defaultNamespaces];

// Query cannot be part of this context because it is used by SearchForm which is in the header and thus ouotside of this context...
type ContextProps = PlcPageContextProps<Omit<SearchPageProps, 'query' | 'searchResults'> & ContentProps> & {
  nextResults: {
    fetchNextResults?: () => void;
    isValidating: boolean;
  };
};

export const SearchContext = createContext<ContextProps>({
  activities: { isValidating: false },
  contentType: { isValidating: false },
  counts: { isValidating: false },
  interests: { isValidating: false },
  nextResults: { isValidating: false },
  pagination: { isValidating: false },
  results: { isValidating: false },
  thematics: { isValidating: false },
});

export function fetchResults(params: Omit<SearchParams, 'contentTypes'> & { contentType?: SearchContentType | null }) {
  const {
    thematics,
    activities,
    contentType,
    interests,
    offset = 0,
    query,
    searchType = SearchType.Prioritize,
    limit,
  } = params;
  return searchContent({
    activities,
    contentTypes: getSearchContentTypes(contentType),
    interests,
    limit,
    offset,
    query,
    searchType,
    thematics,
  });
}

const SearchPageWithQuery: PlcNextPage<Partial<SearchPageProps & { resultPerPage: number }>> = (props) => {
  const resultPerPage = props.resultPerPage ? props.resultPerPage : Constants.SEARCH_RESULT_PER_PAGE[0];
  const defaultOffset = useMemo(() => (props.pageNb || 0) * resultPerPage, [props.pageNb]);
  const [offset, setOffset] = useState(defaultOffset);
  const { searchQuery, setSearchQuery } = useContext(GeneralContext);

  useEffect(() => {
    setSearchQuery?.(props.query || '');
  }, []);

  // Need to redeclare a query object to make sure to set offset to 0 in case of query change (otherwise we risk a 404)
  const [query, setQuery] = useState(searchQuery);
  useEffect(() => {
    setOffset(defaultOffset);
    setQuery(searchQuery);
  }, [searchQuery]);

  const [activities, setActivities] = useState(props.activities || []);
  const [interests, setInterests] = useState(props.interests || []);
  const { thematics } = props;
  const [, setContentType] = useState(props.contentType || SearchContentType.All);
  const contentType: SearchContentType | null = (() => {
    if (props && props.contentType) {
      if (props.contentType === contentTypeInUrl.event) {
        return SearchContentType.Event;
      }
      if (props.contentType === contentTypeInUrl.guide) {
        return SearchContentType.Guide;
      }
      if (props.contentType === contentTypeInUrl.service) {
        return SearchContentType.Service;
      }
      if (props.contentType === contentTypeInUrl.article) {
        return SearchContentType.Article;
      }

      return SearchContentType.All;
    }
    return SearchContentType.All;
  })();
  const { fetchNextResults, pagination, isNextResultsValidating, data, results, isLoadingResults } = useSearchFetching({
    activities,
    contentType,
    defaultOffset,
    initialData: props.searchResults,
    interests,
    limit: resultPerPage,
    offset,
    query,
    setOffset,
    thematics,
  });

  useSearchRouteQuery(interests, activities, contentType, thematics, searchQuery, props.pageNb);

  const setActivitiesFilter: typeof setActivities = (newActivities) => {
    setOffset(defaultOffset);
    setActivities(newActivities);
  };

  const setInterestsFilter: typeof setInterests = (newInterests) => {
    setOffset(defaultOffset);
    setInterests(newInterests);
  };

  const setContentTypeFilter: typeof setContentType = (newContentType) => {
    setOffset(defaultOffset);
    setContentType(newContentType);
  };

  return (
    <SearchContext.Provider
      value={{
        activities: { data: activities, isValidating: false, setData: setActivitiesFilter },
        contentType: { data: contentType, isValidating: false, setData: setContentTypeFilter },
        counts: { data: data?.contentTypesCount, isValidating: isLoadingResults },
        interests: { data: interests, isValidating: false, setData: setInterestsFilter },
        nextResults: { fetchNextResults, isValidating: isNextResultsValidating },
        pagination: { data: pagination, isValidating: isLoadingResults },
        results: { data: results, isValidating: isLoadingResults },
        thematics: { data: thematics, isValidating: false },
      }}
    >
      <Head>
        <meta content="7200" httpEquiv="refresh" />
      </Head>
      <SearchLayout />
    </SearchContext.Provider>
  );
};

export function getServerSideProps({ query, locale, req }: PlcNextPageContext) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { mots_cles, activites, centresInterets, thematiques, page } = query;
  const isServer = !!req;
  const userAgent = isServer && req ? req.headers['user-agent'] : navigator.userAgent;
  const isMobile = userAgent ? /(iPad|iPhone|Android|Mobile)/i.test(userAgent) : false;
  const resultPerPage = isMobile ? Constants.SEARCH_RESULT_PER_PAGE[0] : Constants.SEARCH_RESULT_PER_PAGE[1];

  const searchQuery = castUrlParamToString(mots_cles);

  const contentType: SearchContentType = query.type_de_contenu
    ? (query.type_de_contenu as SearchContentType)
    : SearchContentType.All;

  const activitiesIndexes = castUrlParamToString(activites)
    ?.split(',')
    .map((v) => Object.values(activityInUrl).findIndex((a) => a === v));
  const activities = activitiesIndexes
    ? transformUrlParamToEnumArray<ActivityCode>(
        activitiesIndexes.map((v) => Object.keys(activityInUrl)[v]),
        ActivityCode
      )
    : [];

  const thematicsIndexes = castUrlParamToString(thematiques)
    ?.split(',')
    .map((v) => Object.values(thematicInUrl).findIndex((a) => a === v));
  const thematics = thematicsIndexes
    ? transformUrlParamToEnumArray<ThematicCode>(
        thematicsIndexes.map((v) => Object.keys(thematicInUrl)[v]),
        ThematicCode
      )
    : [];

  const interestsIndexes = castUrlParamToString(centresInterets)
    ?.split(',')
    .map((v) => Object.values(interestInUrl).findIndex((i) => i === v));
  const interests = interestsIndexes
    ? transformUrlParamToEnumArray<InterestCode>(
        interestsIndexes.map((v) => Object.keys(interestInUrl)[v]),
        InterestCode
      )
    : [];

  const pageNb = page ? Number(castUrlParamToString(page)) : null;

  const pageDefaultState = {
    activities,
    contentType,
    interests,
    pageNb,
    query: searchQuery ?? null,
    resultPerPage,
    thematics,
  };

  return Promise.all([serverSideTranslations(locale || 'fr', defaultNamespaces)])
    .then(([i18n]) => {
      return {
        props: { ...pageDefaultState, ...i18n },
      };
    })
    .catch(async (err) => {
      console.error(err);
      return { props: { ...pageDefaultState, ...(await serverSideTranslations(locale || 'fr', NAMESPACES)) } };
    });
}

export default SearchPageWithQuery;
